import { useState, useCallback, useEffect, useRef, useReducer } from 'react';
import {
  FingerConfig,
  Letter
} from '@/types';
import { TrainingGenerator, createTrainingGenerator, createEnhancedTrainingGenerator, EnhancedTrainingGenerator, TrainingMode } from '@/utils/trainingGenerator';
import { animationManager } from '@/utils/animationUtils';
import { triggerCorrectFeedback, triggerCorrectSoundOnly, triggerIncorrectFeedback, clearRedBackground } from '@/components/GameScreen/FeedbackSystem';
import {
  gameStateReducer,
  initialGameState
} from './gameStateReducer';
// 🔥 SPRINT 2: Firebase integration
import { useAuth } from '@/contexts/AuthContext';
// 🔥 SPRINT 2: Course Integration
import {
  getEffectiveTrainingConfiguration,
  logTrainingConfiguration
} from '@/services/courseIntegrationService';
import {
  createRound,
  updateRoundStats,
  initializeWordStats
} from '@/services';
import {
  RoundStatisticsCalculator,
  calculateSessionStats
} from '@/utils/statisticsCalculator';
import { CreateRoundData } from '@/types/firebase';

// 🔧 PHASE 1 FIX: Stable timer management class
class StableTimerManager {
  private startTime: number | null = null;
  private intervalId: number | null = null;
  private isRunning = false;
  private onUpdate: (timeRemaining: number) => void;
  private onComplete: () => void;
  private duration: number;

  constructor(
    duration: number,
    onUpdate: (timeRemaining: number) => void,
    onComplete: () => void
  ) {
    this.duration = duration;
    this.onUpdate = onUpdate;
    this.onComplete = onComplete;
  }

  start() {
    if (this.isRunning) return;

    this.startTime = performance.now();
    this.isRunning = true;

    // 🔧 CRITICAL FIX: Use performance.now() for accurate timing
    this.intervalId = window.setInterval(() => {
      if (!this.startTime || !this.isRunning) return;

      const elapsed = performance.now() - this.startTime;
      const remaining = Math.max(0, this.duration - elapsed);

      this.onUpdate(remaining);

      if (remaining === 0) {
        this.stop();
        this.onComplete();
      }
    }, 100); // 🔧 PERFORMANCE: 100ms for good accuracy with less frequent updates
  }

  pause() {
    if (!this.isRunning || !this.startTime) return;

    this.isRunning = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Store elapsed time
    const elapsed = performance.now() - this.startTime;
    this.duration = Math.max(0, this.duration - elapsed);
  }

  resume() {
    if (this.isRunning) return;
    this.start();
  }

  stop() {
    this.isRunning = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.startTime = null;
  }

  cleanup() {
    this.stop();
  }

  getRemainingTime(): number {
    if (!this.isRunning || !this.startTime) return this.duration;

    const elapsed = performance.now() - this.startTime;
    return Math.max(0, this.duration - elapsed);
  }
}

// 🔧 PHASE 1 FIX: Memory management utility
class MemoryManager {
  private timers: Set<number> = new Set();
  private generators: Set<TrainingGenerator> = new Set();
  private animationIds: Set<string> = new Set();

  registerTimer(timerId: number) {
    this.timers.add(timerId);
  }

  registerGenerator(generator: TrainingGenerator | EnhancedTrainingGenerator) {
    this.generators.add(generator as TrainingGenerator);
  }

  registerAnimation(animationId: string) {
    this.animationIds.add(animationId);
  }

  cleanup() {
    // Clear all timers
    this.timers.forEach(id => clearInterval(id));
    this.timers.clear();

    // Clear animations
    animationManager.clearAllAnimations();
    this.animationIds.clear();

    // Clear generators (if they have cleanup methods)
    this.generators.clear();
  }
}

const ROUND_DURATION = 120000; // 2 minutes

export const useGameEngine = () => {
  // 🔧 CRITICAL FIX: Use reducer pattern instead of complex useState
  const [gameState, dispatch] = useReducer(gameStateReducer, initialGameState);

  const timerManagerRef = useRef<StableTimerManager | null>(null);
  const generatorRef = useRef<TrainingGenerator | EnhancedTrainingGenerator | null>(null);
  const memoryManagerRef = useRef<MemoryManager>(new MemoryManager());
  const sequenceExtensionInProgress = useRef(false);

  // 🔥 SPRINT 2: Firebase integration
  const { user } = useAuth();
  const statisticsCalculatorRef = useRef<RoundStatisticsCalculator>(new RoundStatisticsCalculator());
  const currentRoundIdRef = useRef<string | null>(null);
  const [isFirebaseSyncing, setIsFirebaseSyncing] = useState(false);

  /**
   * 🔧 CRITICAL FIX: Stable timer callbacks
   */
  const handleTimerUpdate = useCallback((timeRemaining: number) => {
    dispatch({ type: 'UPDATE_TIMER', payload: { timeRemaining } });
  }, []);

  const handleTimerComplete = useCallback(() => {
    dispatch({ type: 'END_SESSION' });
  }, []);

  /**
   * 🔥 SPRINT 2: Create Firebase round with exact schema
   */
  const createFirebaseRound = useCallback(async (
    fingerConfig: FingerConfig,
    trainingMode?: TrainingMode,
    dayNumber?: number,
    roundNumber?: number,
    trainingSystemMode?: 'course' | 'custom' // NEW: Pass the actual training system mode
  ): Promise<string | null> => {
    if (!user?.uid) {
      console.warn('⚠️ No authenticated user for Firebase round creation');
      return null;
    }

    try {
      setIsFirebaseSyncing(true);

      // Determine training context - FIXED: Use trainingSystemMode to determine type
      console.log('🔍 Training context determination:', {
        dayNumber,
        roundNumber,
        trainingMode: trainingMode?.complexity,
        trainingSystemMode
      });

      // FIXED: Use the actual training system mode from App.tsx
      const isCourseTraining = trainingSystemMode === 'course';
      const finalTrainingType = isCourseTraining ? 'defTraining' : 'customTraining';

      console.log('✅ Training type determined:', {
        isCourseTraining,
        finalTrainingType,
        willSetDefFields: isCourseTraining
      });

      // Map finger configuration to specification format
      const activeFingers = [...fingerConfig.activeFingersLeft, ...fingerConfig.activeFingersRight];

      // Determine hands setting
      const hasLeftFingers = fingerConfig.activeFingersLeft.length > 0;
      const hasRightFingers = fingerConfig.activeFingersRight.length > 0;

      let hands: 'left' | 'right' | 'both';
      if (hasLeftFingers && hasRightFingers) {
        // Both hands have active fingers
        hands = 'both';
      } else {
        hands = hasLeftFingers ? 'left' : 'right';
      }

      console.log('🔍 Hands determination:', {
        hasLeftFingers,
        hasRightFingers,
        leftCount: fingerConfig.activeFingersLeft.length,
        rightCount: fingerConfig.activeFingersRight.length,
        selectedHands: hands
      });

      // Map complexity
      const { max } = fingerConfig.letterComplexity;
      let complexity: '1' | '1_3' | '1_5' | 'w' = '1';
      if (trainingMode?.complexity === 'words') {
        complexity = 'w';
      } else if (max <= 1) {
        complexity = '1';
      } else if (max <= 3) {
        complexity = '1_3';
      } else {
        complexity = '1_5';
      }

      const roundData: CreateRoundData = {
        trainingType: finalTrainingType,
        defTrainingDay: isCourseTraining ? dayNumber : undefined,
        defTrainingRound: isCourseTraining ? roundNumber : undefined,
        fingers: activeFingers,
        hands,
        complexity,
        duration: ROUND_DURATION,
        letters: {}, // Will be populated during gameplay
        words: initializeWordStats(),
        overallStats: {
          totalLetters: 0,
          correctLetters: 0,
          incorrectLetters: 0,
          totalAttempts: 0,
          wrongAttempts: 0,
          accuracy: 0,
          speed: 0,
          duration: 0,
          wpm: 0
        }
      };

      console.log('📤 Round data created:', {
        trainingType: roundData.trainingType,
        defTrainingDay: roundData.defTrainingDay,
        defTrainingRound: roundData.defTrainingRound,
        fingers: roundData.fingers,
        hands: roundData.hands,
        complexity: roundData.complexity
      });

      const result = await createRound(user.uid, roundData);

      if (result.success && result.data) {
        console.log('✅ Firebase round created:', result.data.roundId);
        return result.data.roundId;
      } else {
        console.error('🚫 Failed to create Firebase round:', result.error);
        return null;
      }
    } catch (error) {
      console.error('🚫 Error creating Firebase round:', error);
      return null;
    } finally {
      setIsFirebaseSyncing(false);
    }
  }, [user]);

  /**
   * 🔥 SPRINT 2: Update Firebase round with current statistics
   */
  const updateFirebaseRound = useCallback(async (roundId: string) => {
    if (!user?.uid || !gameState.session) {
      return;
    }

    try {
      const currentStats = statisticsCalculatorRef.current.getCurrentStats();
      // 🔧 FIX: Use consistent timestamp source and ensure positive duration
      const sessionStartTime = gameState.session.startTime; // This is from Date.now()
      const currentTime = Date.now(); // Use same timestamp source
      const duration = Math.abs(currentTime - sessionStartTime); // Ensure positive

      // Calculate overall stats from current game state
      const overallStats = calculateSessionStats(
        currentStats.letters,
        currentStats.words,
        duration
      );

      console.log('📤 Uploading stats to Firebase:', {
        roundId,
        letterCount: Object.keys(currentStats.letters).length,
        wordsTotal: currentStats.words.wordsTotal,
        overallAccuracy: overallStats.accuracy,
        duration: duration,
        sessionStartTime,
        currentTime
      });

      await updateRoundStats(roundId, {
        letters: currentStats.letters,
        words: currentStats.words,
        overallStats,
        duration
      });

      console.log('📈 Firebase round updated successfully:', roundId);
    } catch (error) {
      console.error('🚫 Error updating Firebase round:', error);
    }
  }, [user, gameState.session]);

  /**
   * 🔥 SPRINT 2: Complete Firebase round with final statistics
   */
  const completeFirebaseRound = useCallback(async (roundId: string) => {
    if (!user?.uid || !gameState.session) {
      return;
    }

    try {
      setIsFirebaseSyncing(true);

      const finalStats = statisticsCalculatorRef.current.completeRound(gameState.stats);
      // 🔧 FIX: Calculate duration consistently using Date.now() timestamps
      const sessionStartTime = gameState.session.startTime; // From Date.now()
      const currentTime = Date.now(); // Use same source
      const totalDuration = gameState.session.endTime
        ? Math.abs(gameState.session.endTime - sessionStartTime)
        : Math.abs(currentTime - sessionStartTime);

      console.log('🏁 Completing Firebase round with final stats:', {
        roundId,
        finalLetterCount: Object.keys(finalStats.letters).length,
        finalWordsTotal: finalStats.words.wordsTotal,
        finalAccuracy: finalStats.overallStats.accuracy,
        totalDuration
      });

      await updateRoundStats(roundId, {
        letters: finalStats.letters,
        words: finalStats.words,
        overallStats: finalStats.overallStats,
        duration: totalDuration
      });

      console.log('✅ Firebase round completed successfully:', roundId);
      currentRoundIdRef.current = null;
    } catch (error) {
      console.error('🚫 Error completing Firebase round:', error);
    } finally {
      setIsFirebaseSyncing(false);
    }
  }, [user, gameState.session, gameState.stats]);

  /**
   * 🆕 NEW: Extract manual word selection from custom configuration
   */
  const extractManualSelection = (customConfig?: any) => {
    if (!customConfig || !customConfig.trainingSelection) {
      return undefined;
    }

    const { trainingSelection } = customConfig;

    // Check if it's manual word training selection
    if (trainingSelection.mode === 'custom' &&
      trainingSelection.wordTraining?.manual) {
      console.log('🎯 Manual word selection found:', {
        selectedWords: trainingSelection.wordTraining.manual.selectedWords?.length || 0,
        selectedPhrases: trainingSelection.wordTraining.manual.selectedPhrases?.length || 0,
        randomizeOrder: trainingSelection.wordTraining.manual.randomizeOrder
      });

      return {
        mode: 'manual' as const,
        manual: {
          selectedWords: trainingSelection.wordTraining.manual.selectedWords || [],
          selectedPhrases: trainingSelection.wordTraining.manual.selectedPhrases || [],
          randomizeOrder: trainingSelection.wordTraining.manual.randomizeOrder || false
        }
      };
    }

    // Check if it's random word training selection
    if (trainingSelection.mode === 'custom' &&
      trainingSelection.wordTraining?.random) {
      console.log('🎲 Random word selection found:', trainingSelection.wordTraining.random);

      return {
        mode: 'random' as const,
        random: trainingSelection.wordTraining.random
      };
    }

    console.log('📋 No manual/random selection found, using default');
    return undefined;
  };

  /**
   * 🆕 NEW: Convert admin selection to session manual selection
   */
  const convertToSessionManualSelection = (adminSelection?: any) => {
    if (!adminSelection || adminSelection.mode !== 'manual' || !adminSelection.manual) {
      return undefined;
    }

    return {
      selectedWords: adminSelection.manual.selectedWords || [],
      selectedPhrases: adminSelection.manual.selectedPhrases || [],
      randomizeOrder: adminSelection.manual.randomizeOrder || false
    };
  };

  /**
   * 🔧 ENHANCED: Stable sequence generation with async support and manual selection context
   */
  const extendSequencesIfNeeded = useCallback(async () => {
    if (sequenceExtensionInProgress.current || !generatorRef.current) {
      return;
    }

    const currentSession = gameState.session;
    if (!currentSession) return;

    const currentIndex = currentSession.currentSequenceIndex;
    const totalSequences = currentSession.letterSequences.length;
    const remainingSequences = totalSequences - currentIndex;

    // 🔧 CRITICAL: Generate more sequences when running low
    if (remainingSequences <= 5) {
      sequenceExtensionInProgress.current = true;

      try {
        const fingerConfig = currentSession.activeFingersConfig;
        const maxComplexity = fingerConfig.letterComplexity.max;

        let additionalSequences: Letter[][];

        // 🆕 NEW: Check if we have manual selection context for word mode
        if (currentSession.manualSelection && currentSession.trainingMode?.complexity === 'words') {
          console.log('🔁 [WORD REPETITION] Extending with manual selection context:', {
            wordsCount: currentSession.manualSelection.selectedWords.length,
            phrasesCount: currentSession.manualSelection.selectedPhrases.length,
            randomizeOrder: currentSession.manualSelection.randomizeOrder
          });

          // Use manual selection for additional sequences
          const manualSelectionForGenerator = {
            mode: 'manual' as const,
            manual: currentSession.manualSelection
          };

          // Check if generator supports async operations with manual selection
          if (typeof (generatorRef.current as any).generateMoreSequencesWithManualSelection === 'function') {
            const generator = generatorRef.current as any;
            additionalSequences = await generator.generateMoreSequencesWithManualSelection(maxComplexity, 30, manualSelectionForGenerator);
          } else if (typeof (generatorRef.current as any).generateLetterSequences === 'function') {
            // Fallback: regenerate using the same manual selection
            const generator = generatorRef.current as any;
            try {
              additionalSequences = await generator.generateLetterSequences(maxComplexity, manualSelectionForGenerator);
              console.log('🔄 [WORD REPETITION] Used fallback generateLetterSequences for extension');
            } catch (error) {
              console.warn('⚠️ Fallback generateLetterSequences failed:', error);
              additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
            }
          } else {
            // Final fallback to base synchronous generation
            additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
          }
        } else {
          // Standard generation without manual selection
          console.log('📝 [STANDARD] Extending with standard generation (no manual selection)');

          const generator = generatorRef.current as any;

          // FIX: For word mode, always use generateWordBasedSequences (DB lookup + pseudo-word fallback)
          // instead of generateMoreSequences which generates random single letters
          if (currentSession.trainingMode?.complexity === 'words' &&
              typeof generator.generateWordBasedSequences === 'function') {
            try {
              console.log('🔁 [WORD MODE] Extending with word-based generation (DB + pseudo-words)');
              additionalSequences = await generator.generateWordBasedSequences(maxComplexity);
            } catch (error) {
              console.warn('⚠️ Word-based extension failed, using pseudo-word fallback:', error);
              additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
            }
          } else if (typeof generator.generateMoreSequences === 'function') {
            try {
              additionalSequences = await generator.generateMoreSequences(maxComplexity, 30);
            } catch (error) {
              console.warn('⚠️ Async extension failed, using sync fallback:', error);
              additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
            }
          } else {
            additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
          }
        }

        if (additionalSequences && additionalSequences.length > 0) {
          dispatch({
            type: 'EXTEND_SEQUENCES',
            payload: { newSequences: additionalSequences }
          });

          console.log(`✅ Extended training with ${additionalSequences.length} new sequences`);
        } else {
          console.warn('⚠️ Failed to generate additional sequences - training may end early');
        }
      } catch (error) {
        console.error('❌ Failed to extend sequences:', error);
        // Don't end the session, just continue with remaining sequences
      } finally {
        sequenceExtensionInProgress.current = false;
      }
    }
  }, []); // 🚨 SPRINT 1 FIX: Empty dependencies for stability

  /**
   * 🔧 ENHANCED: Start session with Course Integration, async word support, and optimized animations
   */
  const startSession = useCallback(async (
    fingerConfig: FingerConfig,
    dayNumber: number,
    trainingMode?: TrainingMode,
    roundNumber?: number,
    trainingSystemMode?: 'course' | 'custom',
    levelNumber?: number,
    manualSelectionOverride?: { selectedWords: string[]; selectedPhrases: string[]; randomizeOrder: boolean }
  ) => {
    console.log('🚀 Starting session with Course Integration, Firebase, word support, and optimized animations');

    // 🔥 NEW: Course Integration - check for saved configurations
    let effectiveFingerConfig = fingerConfig;
    let effectiveTrainingMode = trainingMode;
    let customConfig: any = undefined; // Store the full custom config

    if (trainingSystemMode === 'course' && dayNumber && roundNumber) {
      console.log(`🔍 Course mode detected - checking for custom configuration Level ${levelNumber}, Day ${dayNumber}, Round ${roundNumber}`);

      try {
        const configResult = await getEffectiveTrainingConfiguration(levelNumber || 1, dayNumber, roundNumber);

        if (configResult.isCustom) {
          console.log('✅ Using CUSTOM configuration from admin panel');
          effectiveFingerConfig = configResult.fingerConfig;
          effectiveTrainingMode = configResult.trainingMode;
          customConfig = configResult.customConfig; // 🆕 Store for manual selection extraction

          // Log the configuration for debugging
          logTrainingConfiguration(dayNumber, roundNumber, {
            fingerConfig: effectiveFingerConfig,
            trainingMode: effectiveTrainingMode,
            isCustom: true
          });
        } else {
          console.log('📚 Using DEFAULT course structure');
          effectiveFingerConfig = configResult.fingerConfig;

          logTrainingConfiguration(dayNumber, roundNumber, {
            fingerConfig: effectiveFingerConfig,
            trainingMode: effectiveTrainingMode,
            isCustom: false
          });
        }
      } catch (error) {
        console.error('❌ Error loading course configuration, using provided config:', error);
        // Continue with provided configuration
      }
    }

    // Clean up previous session
    timerManagerRef.current?.cleanup();
    memoryManagerRef.current.cleanup();
    statisticsCalculatorRef.current.reset();

    // Create training generator with effective configuration
    const generator = effectiveTrainingMode
      ? createEnhancedTrainingGenerator(effectiveFingerConfig, effectiveTrainingMode)
      : createTrainingGenerator(effectiveFingerConfig);

    console.log('🔍 [INVESTIGATION] Generator creation:', {
      hasTrainingMode: !!effectiveTrainingMode,
      generatorType: effectiveTrainingMode ? 'EnhancedTrainingGenerator' : 'TrainingGenerator',
      trainingModeComplexity: effectiveTrainingMode?.complexity,
      isWordMode: effectiveTrainingMode?.complexity === 'words'
    });

    generatorRef.current = generator;
    memoryManagerRef.current.registerGenerator(generator);

    // Generate initial sequences with async support for word mode
    const maxComplexity = effectiveFingerConfig.letterComplexity.max;
    let letterSequences: Letter[][];

    // 🆕 Extract manual selection from custom config for word training,
    // or use direct override (used by One Word mode)
    const manualSelection = manualSelectionOverride
      ? { mode: 'manual' as const, manual: manualSelectionOverride }
      : extractManualSelection(customConfig);
    const sessionManualSelection = convertToSessionManualSelection(manualSelection);

    console.log('🔍 [MANUAL SELECTION] Extracted for session:', {
      hasManualSelection: !!sessionManualSelection,
      wordsCount: sessionManualSelection?.selectedWords?.length || 0,
      phrasesCount: sessionManualSelection?.selectedPhrases?.length || 0,
      randomizeOrder: sessionManualSelection?.randomizeOrder
    });

    try {
      if (generator instanceof EnhancedTrainingGenerator) {
        // EnhancedTrainingGenerator - use async method for manual selection
        console.log('🎯 [INVESTIGATION] Using EnhancedTrainingGenerator (async for both words and letters)');
        console.log('🎯 [INVESTIGATION] About to call generateLetterSequencesAsync with maxComplexity:', maxComplexity);
        console.log('🆕 [INVESTIGATION] Passing manual selection:', manualSelection);
        letterSequences = await generator.generateLetterSequencesAsync(maxComplexity, manualSelection);
        console.log('🎯 [INVESTIGATION] EnhancedTrainingGenerator returned', letterSequences.length, 'sequences');
      } else {
        // Base TrainingGenerator returns synchronously
        console.log('📝 [INVESTIGATION] Using base TrainingGenerator (synchronous)');
        console.log('📝 [INVESTIGATION] About to call generateLetterSequences with maxComplexity:', maxComplexity);
        letterSequences = generator.generateLetterSequences(maxComplexity);
        console.log('📝 [INVESTIGATION] Base TrainingGenerator returned', letterSequences.length, 'sequences');
      }
    } catch (error) {
      console.error('❌ [INVESTIGATION] Error generating sequences:', error);
      // Fallback to basic generation
      if (generator instanceof EnhancedTrainingGenerator) {
        letterSequences = await generator.generateLetterSequencesAsync(maxComplexity, manualSelection);
      } else {
        letterSequences = generator.generateLetterSequences(maxComplexity);
      }
    }

    if (letterSequences.length === 0) {
      console.error('❌ [INVESTIGATION] No letter sequences generated for training session');
      return;
    }

    console.log(`✅ [INVESTIGATION] Session created with ${letterSequences.length} sequences using ${effectiveFingerConfig === fingerConfig ? 'original' : 'custom'} configuration`);
    console.log('🔍 [INVESTIGATION] First 5 sequences going to game state (should be COMPLETE WORDS in word mode):');
    letterSequences.slice(0, 5).forEach((seq, i) => {
      console.log(`  [INVESTIGATION] Sequence ${i + 1}: "${seq.map(l => l.char).join('')}" (${seq.length} letters)`);
    });

    // 🔥 SPRINT 2: Create Firebase round
    const roundId = await createFirebaseRound(effectiveFingerConfig, effectiveTrainingMode, dayNumber, roundNumber, trainingSystemMode);
    if (roundId) {
      currentRoundIdRef.current = roundId;
    }

    // 🔥 SPRINT 2: Initialize statistics calculator with CORRECT data types
    const allLetterObjects: Letter[] = letterSequences.flat();
    const allLetterChars: string[] = allLetterObjects.map(letter => letter.char);
    const complexityString = effectiveTrainingMode?.complexity === 'words' ? 'w' : effectiveFingerConfig.letterComplexity.max.toString();
    const isWordMode = effectiveTrainingMode?.complexity === 'words';

    console.log('🔍 Statistics initialization:', {
      totalSequences: letterSequences.length,
      totalLetters: allLetterObjects.length,
      uniqueLetterChars: Array.from(new Set(allLetterChars)),
      complexityString,
      isWordMode,
      usingCustomConfig: effectiveFingerConfig !== fingerConfig
    });

    // 🎯 NEW: Configure animation manager for training mode
    animationManager.setTrainingMode(
      isWordMode ? 'word' : 'letter',
      letterSequences[0], // First sequence
      0 // Starting index
    );
    console.log(`🎬 Animation manager set to ${isWordMode ? 'WORD' : 'LETTER'} mode`);

    statisticsCalculatorRef.current.startRound(allLetterObjects, complexityString);

    // 🔧 CRITICAL FIX: Use reducer to start session with effective config
    dispatch({
      type: 'START_SESSION',
      payload: {
        fingerConfig: effectiveFingerConfig, // Use the effective config
        dayNumber,
        trainingMode: effectiveTrainingMode, // Use the effective training mode
        roundNumber,
        letterSequences,
        manualSelection: sessionManualSelection // 🆕 NEW: Pass manual selection context
      }
    });

    // 🔧 CRITICAL FIX: Start stable timer
    timerManagerRef.current = new StableTimerManager(
      ROUND_DURATION,
      handleTimerUpdate,
      handleTimerComplete
    );
    timerManagerRef.current.start();

  }, [handleTimerUpdate, handleTimerComplete, createFirebaseRound]);

  /**
   * 🔧 SPRINT 1 TASK 1.3: FINAL processKeystroke optimization with word-mode animations
   */
  const processKeystroke = useCallback((key: string) => {
    if (!gameState.isActive || !gameState.session || !gameState.currentSequence) {
      return;
    }

    const timestamp = Date.now(); // 🔧 FIX: Use Date.now() for consistency with session.startTime

    // Get current letter for statistics tracking
    const currentLetter = gameState.currentSequence[gameState.currentSequenceIndex];
    if (currentLetter) {
      const expectedChar = currentLetter.char;
      const normalizedKey = key === ' ' ? '_' : key.toUpperCase();
      const normalizedExpected = expectedChar === '_' ? '_' : expectedChar.toUpperCase();
      const isCorrect = normalizedKey === normalizedExpected;

      // 🔥 SPRINT 2: Record keystroke for statistics (REDUCED LOGGING)
      statisticsCalculatorRef.current.recordKeystroke(expectedChar, normalizedKey, isCorrect);

      // 🎯 ENHANCED: Word-mode optimized visual feedback
      const letterId = `letter-${currentLetter.id}`;
      const isWordsMode = gameState.session.trainingMode?.complexity === 'words';

      if (isCorrect) {
        // 🎯 GREEN ANIMATIONS: Optimized for word mode
        animationManager.triggerGreenFlash(letterId); // This will auto-skip in word mode

        // 🎯 NEW: Check for word completion in word mode
        const isWordComplete = isWordsMode && animationManager.checkWordCompletion(
          gameState.currentSequenceIndex,
          gameState.currentSequence
        );

        if (isWordsMode) {
          if (isWordComplete) {
            triggerCorrectFeedback();
            console.log('🎉 Word completed! Triggered celebration animation and sound');
          } else {
            triggerCorrectSoundOnly();
          }
        } else {
          triggerCorrectFeedback();
        }

        clearRedBackground();
      } else {
        // 🎯 RED ANIMATIONS: Always show for incorrect letters (both modes)
        animationManager.triggerRedDistortion(letterId);
        triggerIncorrectFeedback();
      }

      // Register animation for cleanup
      memoryManagerRef.current.registerAnimation(letterId);
    }

    // 🔧 CRITICAL FIX: Use reducer for keystroke processing
    dispatch({
      type: 'PROCESS_KEYSTROKE',
      payload: { key, timestamp }
    });

    // 🚨 SPRINT 1 COMPLETE: ALL infinite loop sources removed:
    // ❌ Firebase auto-save (now in separate interval)
    // ❌ extendSequencesIfNeeded (now in separate effect)
    // ❌ Complex dependencies (minimized to essential state only)

  }, [gameState.isActive, gameState.session, gameState.currentSequence, gameState.currentSequenceIndex]); // 🚨 SPRINT 1: MINIMAL, ESSENTIAL DEPENDENCIES ONLY

  /**
   * 🎯 NEW: Update animation manager when sequence changes
   */
  useEffect(() => {
    if (gameState.currentSequence && gameState.session) {
      // Update animation manager with current sequence and index
      animationManager.setTrainingMode(
        gameState.session.trainingMode?.complexity === 'words' ? 'word' : 'letter',
        gameState.currentSequence,
        gameState.currentSequenceIndex
      );
    }
  }, [gameState.currentSequence, gameState.currentSequenceIndex, gameState.session?.trainingMode]);

  /**
   * 🔧 CRITICAL FIX: Stable session end with Firebase completion
   */
  const endSession = useCallback(async () => {
    timerManagerRef.current?.stop();
    dispatch({ type: 'END_SESSION' });

    // 🔥 SPRINT 2: Complete Firebase round
    if (currentRoundIdRef.current) {
      await completeFirebaseRound(currentRoundIdRef.current);
    }
  }, [completeFirebaseRound]);

  /**
   * 🔧 PERFORMANCE FIX: Stable pause/resume
   */
  const pauseSession = useCallback(() => {
    timerManagerRef.current?.pause();
    dispatch({ type: 'PAUSE_SESSION' });
  }, []);

  const resumeSession = useCallback(() => {
    timerManagerRef.current?.resume();
    dispatch({ type: 'RESUME_SESSION' });
  }, []);

  /**
   * 🔥 SPRINT 2: Start letter tracking when sequence changes + complete sequences
   */
  useEffect(() => {
    if (gameState.currentSequence && gameState.currentSequenceIndex < gameState.currentSequence.length) {
      const currentLetter = gameState.currentSequence[gameState.currentSequenceIndex];
      if (currentLetter) {
        statisticsCalculatorRef.current.startLetterTracking(currentLetter.char);
      }
    }
  }, [gameState.currentSequence, gameState.currentSequenceIndex]);

  /**
   * 🔥 SPRINT 2: Complete word when sequence transitions (NEW)
   */
  useEffect(() => {
    // When session's sequence index changes, complete the previous sequence's word
    if (gameState.session && gameState.session.currentSequenceIndex > 0) {
      statisticsCalculatorRef.current.completeCurrentSequence();
    }
  }, [gameState.session?.currentSequenceIndex]);

  /**
   * 🔧 CRITICAL FIX: Comprehensive cleanup on unmount
   */
  useEffect(() => {
    return () => {
      timerManagerRef.current?.cleanup();
      memoryManagerRef.current.cleanup();
      statisticsCalculatorRef.current.reset();
      sequenceExtensionInProgress.current = false;
      currentRoundIdRef.current = null;
    };
  }, []);

  /**
   * 🔧 CRITICAL FIX: Monitor when more sequences are needed with async support and manual selection
   */
  useEffect(() => {
    if (gameState.needsMoreSequences && gameState.session && gameState.isActive && generatorRef.current) {
      console.log('🚨 needsMoreSequences flag detected - extending sequences immediately!');

      const fingerConfig = gameState.session.activeFingersConfig;
      const maxComplexity = fingerConfig.letterComplexity.max;

      const extendSequencesAsync = async () => {
        try {
          let additionalSequences: Letter[][];

          // 🆕 NEW: Check if we have manual selection context for word mode
          if (gameState.session?.manualSelection && gameState.session?.trainingMode?.complexity === 'words') {
            console.log('🔁 [WORD REPETITION] needsMoreSequences with manual selection context:', {
              wordsCount: gameState.session.manualSelection.selectedWords.length,
              phrasesCount: gameState.session.manualSelection.selectedPhrases.length,
              randomizeOrder: gameState.session.manualSelection.randomizeOrder
            });

            // Use manual selection for additional sequences
            const manualSelectionForGenerator = {
              mode: 'manual' as const,
              manual: gameState.session.manualSelection
            };

            // Check if generator supports manual selection extension
            if (typeof (generatorRef.current as any).generateMoreSequencesWithManualSelection === 'function') {
              const generator = generatorRef.current as any;
              additionalSequences = await generator.generateMoreSequencesWithManualSelection(maxComplexity, 30, manualSelectionForGenerator);
            } else if (typeof (generatorRef.current as any).generateLetterSequences === 'function') {
              // Fallback: regenerate using the same manual selection
              const generator = generatorRef.current as any;
              try {
                additionalSequences = await generator.generateLetterSequences(maxComplexity, manualSelectionForGenerator);
                console.log('🔄 [WORD REPETITION] Used fallback generateLetterSequences for needsMoreSequences');
              } catch (error) {
                console.warn('⚠️ Fallback generateLetterSequences failed in needsMoreSequences:', error);
                additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
              }
            } else {
              // Final fallback to base synchronous generation
              additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
            }
          } else {
            // Standard generation without manual selection
            console.log('📝 [STANDARD] needsMoreSequences with standard generation (no manual selection)');

            // Check if generator supports async operations (word mode)
            if (generatorRef.current && typeof (generatorRef.current as any).generateMoreSequences === 'function') {
              const generator = generatorRef.current as any;

              // Try async generation first (for word mode)
              try {
                additionalSequences = await generator.generateMoreSequences(maxComplexity, 30);
              } catch (error) {
                console.warn('⚠️ Async generation failed, falling back to sync:', error);
                // Fallback to synchronous generation
                additionalSequences = generator.generateMoreSequences ?
                  generator.generateMoreSequences(maxComplexity, 30) :
                  generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
              }
            } else {
              // Use base generator synchronous method
              additionalSequences = generatorRef.current?.generateMoreSequences(maxComplexity, 30) || [];
            }
          }

          if (additionalSequences && additionalSequences.length > 0) {
            dispatch({
              type: 'EXTEND_SEQUENCES',
              payload: { newSequences: additionalSequences }
            });
            console.log(`✅ Extended with ${additionalSequences.length} new sequences due to needsMoreSequences flag`);
          } else {
            console.error('❌ Failed to generate sequences when needsMoreSequences flag was set');
          }
        } catch (error) {
          console.error('❌ Error extending sequences when needsMoreSequences flag was set:', error);
        }
      };

      extendSequencesAsync();
    }
  }, [gameState.needsMoreSequences, gameState.session?.id, gameState.isActive]);

  /**
   * 🔧 SPRINT 1 FIX: Monitor sequence levels with async support
   */
  useEffect(() => {
    if (gameState.session && gameState.isActive && !gameState.needsMoreSequences) {
      const currentIndex = gameState.session.currentSequenceIndex;
      const totalSequences = gameState.session.letterSequences.length;
      const remainingSequences = totalSequences - currentIndex;

      // Only extend if we're really low on sequences
      if (remainingSequences <= 5) {
        console.log(`⚠️ Low sequences: ${remainingSequences} remaining - triggering async extension`);

        // Execute async extension
        const executeAsyncExtension = async () => {
          try {
            await extendSequencesIfNeeded();
          } catch (error) {
            console.error('❌ Error in async sequence extension:', error);
          }
        };

        executeAsyncExtension();
      }
    }
  }, [gameState.session?.currentSequenceIndex, gameState.isActive, gameState.needsMoreSequences, extendSequencesIfNeeded]);

  /**
   * 🔧 SPRINT 1 TASK 1.3: Optimized Firebase auto-save with debouncing and stable calls
   */
  useEffect(() => {
    if (!gameState.isActive || !currentRoundIdRef.current) {
      return;
    }

    console.log('🔄 Starting optimized Firebase auto-save (every 20 seconds with debouncing)');

    let autoSaveInterval: number;
    let lastSaveTime = 0;
    const MIN_SAVE_INTERVAL = 15000; // Minimum 15 seconds between saves

    const performAutoSave = () => {
      const now = Date.now();

      // 🔧 SPRINT 1: Debounce rapid saves
      if (now - lastSaveTime < MIN_SAVE_INTERVAL) {
        console.log('🔄 Auto-save debounced - too soon since last save');
        return;
      }

      if (currentRoundIdRef.current && gameState.isActive) {
        console.log('🔄 Performing auto-save to Firebase...', {
          roundId: currentRoundIdRef.current,
          sessionActive: gameState.isActive,
          timeSinceLastSave: now - lastSaveTime
        });

        // 🔧 SPRINT 1: Direct call to prevent dependency loops
        updateFirebaseRound(currentRoundIdRef.current);
        lastSaveTime = now;
      }
    };

    // Start the interval
    autoSaveInterval = window.setInterval(performAutoSave, 20000); // Every 20 seconds

    return () => {
      console.log('🔄 Clearing optimized Firebase auto-save interval');
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }
    };
  }, [gameState.isActive]); // 🔧 SPRINT 1: MINIMAL dependency - no function dependencies to prevent loops

  return {
    gameState,
    startSession,
    endSession,
    processKeystroke,
    pauseSession,
    resumeSession,
    isSessionActive: gameState.isActive && gameState.session !== null,
    currentSequence: gameState.currentSequence,
    currentSequenceIndex: gameState.currentSequenceIndex,
    nextSequence: gameState.nextSequence,
    stats: gameState.stats,
    letterMistakes: gameState.letterMistakes,
    timeRemaining: gameState.timeRemaining,
    progressTiles: gameState.progressTiles,
    originalSessionContext: gameState.originalSessionContext,
    // 🔧 DEBUG: Expose needsMoreSequences flag
    needsMoreSequences: gameState.needsMoreSequences,
    // 🔥 SPRINT 2: Firebase integration
    isFirebaseSyncing,
    currentRoundId: currentRoundIdRef.current,
    updateFirebaseRound: currentRoundIdRef.current ? () => updateFirebaseRound(currentRoundIdRef.current!) : null
  };
};
